import {
  formatarIdentificacaoObra,
  type ObraIdentificavel,
} from "@/shared/utils/formatar-identificacao-obra";
import type { FilterDefinition, FilterOption, FilterValue } from "@/ds";
import { findConflitos } from "./carga-de-trabalho.model";
import { isObraCancelada } from "./recursos.types";
import type { FornecedorAlocacoes, ObraAlocacoes, ObraResumo } from "./recursos.types";

/**
 * Filtros da Carga de Trabalho de Fornecedores (US-1774), aplicados no cliente
 * sobre os dados carregados, como na perspectiva Profissionais: as opções vêm
 * dos dados sem filtro, colunas diferentes combinam com E e os valores de uma
 * mesma coluna com OU.
 */

/** Dados carregados, conforme o "Visualizar por". */
export type CargaFornecedorDados =
  | { agrupamento: "fornecedor"; fornecedores: FornecedorAlocacoes[] }
  | { agrupamento: "obra"; obras: ObraAlocacoes[] };

/** Colunas filtráveis (Figma: Filtro - Fornecedor). */
export const CARGA_FORNECEDOR_FILTER = {
  fornecedor: "fornecedor",
  obra: "obra",
  conflito: "conflito",
  cancelada: "cancelada",
} as const;

const COM_CONFLITO = "com";
const SEM_CONFLITO = "sem";
const SIM = "sim";
const NAO = "nao";

const CONFLITO_OPTIONS: FilterOption[] = [
  { value: COM_CONFLITO, label: "Com conflito" },
  { value: SEM_CONFLITO, label: "Sem conflito" },
];

const SIM_NAO: FilterOption[] = [
  { value: SIM, label: "Sim" },
  { value: NAO, label: "Não" },
];

function obraLabel(obra: ObraIdentificavel): string {
  return formatarIdentificacaoObra(obra);
}

function byLabel(a: FilterOption, b: FilterOption): number {
  return a.label.localeCompare(b.label, "pt-BR");
}

function collect(dados: CargaFornecedorDados | null) {
  const fornecedores = new Map<string, string>();
  const obras = new Map<string, ObraResumo>();
  if (!dados) return { fornecedores, obras };

  if (dados.agrupamento === "fornecedor") {
    for (const fornecedor of dados.fornecedores) {
      fornecedores.set(fornecedor.id, fornecedor.nome);
      for (const alocacao of fornecedor.alocacoes) obras.set(alocacao.obra.id, alocacao.obra);
    }
  } else {
    for (const obra of dados.obras) {
      obras.set(obra.id, obra);
      for (const alocacao of obra.alocacoes) {
        fornecedores.set(alocacao.fornecedor.id, alocacao.fornecedor.nome);
      }
    }
  }

  return { fornecedores, obras };
}

/** Colunas do painel; as opções de Fornecedor e Obra vêm dos dados carregados (sem filtro). */
export function buildCargaFornecedorFilterColumns(
  dados: CargaFornecedorDados | null,
): FilterDefinition[] {
  const { fornecedores, obras } = collect(dados);

  return [
    {
      key: CARGA_FORNECEDOR_FILTER.fornecedor,
      label: "Fornecedor",
      type: "multiselect",
      options: [...fornecedores].map(([value, label]) => ({ value, label })).sort(byLabel),
    },
    {
      key: CARGA_FORNECEDOR_FILTER.obra,
      label: "Obra",
      type: "multiselect",
      options: [...obras.values()]
        .sort((a, b) => a.codigo.localeCompare(b.codigo, "pt-BR"))
        .map((obra) => ({ value: obra.id, label: obraLabel(obra) })),
    },
    {
      key: CARGA_FORNECEDOR_FILTER.conflito,
      label: "Conflito",
      type: "select",
      options: CONFLITO_OPTIONS,
    },
    {
      key: CARGA_FORNECEDOR_FILTER.cancelada,
      label: "Obra cancelada",
      type: "select",
      options: SIM_NAO,
    },
  ];
}

export function hasCargaFornecedorFilters(value: FilterValue): boolean {
  return Object.values(value).some((values) => values.length > 0);
}

/** "Obra cancelada = Sim" só mostra algo com o indicador Cancelada ligado. */
export function filtraObraCancelada(value: FilterValue): boolean {
  return value[CARGA_FORNECEDOR_FILTER.cancelada]?.[0] === SIM;
}

function selected(value: FilterValue, key: string): string[] {
  return value[key] ?? [];
}

function matchesCancelada(obra: Pick<ObraResumo, "situacao">, cancelada?: string): boolean {
  return !cancelada || (cancelada === SIM) === isObraCancelada(obra.situacao);
}

type IntervaloDaObra = Parameters<typeof idsEmConflito>[0][number];

/**
 * Mesmo período que o Gantt usa para o selo de conflito: o da obra, e o da alocação
 * quando a obra não traz as datas.
 */
function toIntervalo(
  id: string,
  obra: Pick<ObraResumo, "id" | "situacao" | "obraInicio" | "obraFim">,
  alocacao: { inicio: string; fim: string },
): IntervaloDaObra {
  const temPeriodoObra = Boolean(obra.obraInicio && obra.obraFim);
  return {
    id,
    obraId: obra.id,
    inicio: temPeriodoObra ? (obra.obraInicio as string) : alocacao.inicio,
    fim: temPeriodoObra ? (obra.obraFim as string) : alocacao.fim,
    cancelada: isObraCancelada(obra.situacao),
  };
}

/**
 * Só a sobreposição entre obras distintas é conflito de fornecedor (mesma regra do selo
 * do Gantt, `isConflitoEntreObras`): escopos da mesma obra podem se sobrepor sem conflito.
 * Devolve os ids das alocações envolvidas em algum conflito.
 */
function idsEmConflito(
  alocacoes: { id: string; obraId: string; inicio: string; fim: string; cancelada: boolean }[],
): Set<string> {
  const obraPorAlocacao = new Map(alocacoes.map((item) => [item.id, item.obraId]));
  const ids = new Set<string>();

  for (const { alocacoes: par } of findConflitos(alocacoes)) {
    if (obraPorAlocacao.get(par[0]) === obraPorAlocacao.get(par[1])) continue;
    par.forEach((id) => ids.add(id));
  }

  return ids;
}

function filterPorFornecedor(
  fornecedores: FornecedorAlocacoes[],
  value: FilterValue,
): FornecedorAlocacoes[] {
  const ids = selected(value, CARGA_FORNECEDOR_FILTER.fornecedor);
  const obras = selected(value, CARGA_FORNECEDOR_FILTER.obra);
  const [conflito] = selected(value, CARGA_FORNECEDOR_FILTER.conflito);
  const [cancelada] = selected(value, CARGA_FORNECEDOR_FILTER.cancelada);
  const filtraAlocacoes = obras.length > 0 || Boolean(cancelada);

  return (
    fornecedores
      .filter((fornecedor) => {
        if (ids.length > 0 && !ids.includes(fornecedor.id)) return false;
        // Como em Profissionais, o conflito é do fornecedor, apurado em todas as alocações carregadas.
        if (conflito) {
          const temConflito =
            idsEmConflito(
              fornecedor.alocacoes.map((alocacao) =>
                toIntervalo(alocacao.id, alocacao.obra, alocacao),
              ),
            ).size > 0;
          if ((conflito === COM_CONFLITO) !== temConflito) return false;
        }
        return true;
      })
      .map((fornecedor) => ({
        ...fornecedor,
        alocacoes: fornecedor.alocacoes.filter(
          (alocacao) =>
            (obras.length === 0 || obras.includes(alocacao.obra.id)) &&
            matchesCancelada(alocacao.obra, cancelada),
        ),
      }))
      // Linha sem nenhuma alocação restante sai do Gantt.
      .filter((fornecedor) => !filtraAlocacoes || fornecedor.alocacoes.length > 0)
  );
}

/** Ids das alocações envolvidas em conflito: pares do mesmo fornecedor, em todas as obras. */
function alocacoesEmConflito(obras: ObraAlocacoes[]): Set<string> {
  const porFornecedor = new Map<string, Parameters<typeof idsEmConflito>[0]>();

  for (const obra of obras) {
    for (const alocacao of obra.alocacoes) {
      const key = alocacao.fornecedor.id || alocacao.fornecedor.nome;
      const list = porFornecedor.get(key) ?? [];
      list.push(toIntervalo(alocacao.id, obra, alocacao));
      porFornecedor.set(key, list);
    }
  }

  return new Set([...porFornecedor.values()].flatMap((list) => [...idsEmConflito(list)]));
}

function filterPorObra(obras: ObraAlocacoes[], value: FilterValue): ObraAlocacoes[] {
  const fornecedores = selected(value, CARGA_FORNECEDOR_FILTER.fornecedor);
  const ids = selected(value, CARGA_FORNECEDOR_FILTER.obra);
  const [conflito] = selected(value, CARGA_FORNECEDOR_FILTER.conflito);
  const [cancelada] = selected(value, CARGA_FORNECEDOR_FILTER.cancelada);
  const filtraAlocacoes = fornecedores.length > 0 || Boolean(conflito);
  const emConflito = conflito ? alocacoesEmConflito(obras) : new Set<string>();

  return obras
    .filter(
      (obra) => (ids.length === 0 || ids.includes(obra.id)) && matchesCancelada(obra, cancelada),
    )
    .map((obra) => ({
      ...obra,
      alocacoes: obra.alocacoes.filter(
        (alocacao) =>
          (fornecedores.length === 0 || fornecedores.includes(alocacao.fornecedor.id)) &&
          (!conflito || (conflito === COM_CONFLITO) === emConflito.has(alocacao.id)),
      ),
    }))
    .filter((obra) => !filtraAlocacoes || obra.alocacoes.length > 0);
}

/** Aplica os filtros do painel sobre os dados carregados, no agrupamento em tela. */
export function applyCargaFornecedorFilters<T extends CargaFornecedorDados>(
  dados: T,
  value: FilterValue,
): T {
  if (!hasCargaFornecedorFilters(value)) return dados;
  const filtered: CargaFornecedorDados =
    dados.agrupamento === "fornecedor"
      ? { ...dados, fornecedores: filterPorFornecedor(dados.fornecedores, value) }
      : { ...dados, obras: filterPorObra(dados.obras, value) };
  return filtered as T;
}
